import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const toSeasonYear = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toSeasonList = (value) => {
  if (!Array.isArray(value)) return [];
  const years = value.map(toSeasonYear).filter(y => y !== null);
  return Array.from(new Set(years)).sort((a, b) => b - a);
};

// Rendering a page outside the provider (isolated tests, embedded widgets) must not throw.
// `entriesOpen` is null rather than false until the server answers, so a consumer with its
// own site-time read can tell "closed" apart from "not known yet".
const DEFAULT_SEASON_STATE = {
  activeSeason: null,
  availableSeasons: [],
  nowIso: '',
  entriesOpen: null,
  entryFee: null,
  dataVersion: 0,
  loading: false,
  error: '',
  refresh: async () => {}
};

const SeasonContext = createContext(null);

export function SeasonProvider({ children }) {
  const [state, setState] = useState({
    activeSeason: null,
    availableSeasons: [],
    nowIso: '',
    entriesOpen: null,
    entryFee: null,
    dataVersion: 0,
    loading: true,
    error: ''
  });

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/site-time`);
      const ct = res.headers.get('content-type') || '';
      const data = ct.includes('application/json') ? await res.json().catch(() => ({})) : {};
      if (!res.ok) throw new Error(data?.error || 'Failed to load site time');
      const activeSeason = toSeasonYear(data.active_season);
      const availableSeasons = toSeasonList(data.available_seasons);
      const feeRaw = data.entry_fee;
      const feeNum = feeRaw == null || feeRaw === '' ? null : Number(feeRaw);
      const parsedVersion = typeof data.data_version === 'number'
        ? data.data_version
        : parseInt(data.data_version, 10);
      const dataVersion = Number.isFinite(parsedVersion) ? parsedVersion : 0;

      setState(prev => {
        const nextActiveSeason = activeSeason;
        const nextAvailableSeasons = availableSeasons.length === 0 && activeSeason !== null ? [activeSeason] : availableSeasons;
        const nextNowIso = typeof data.now_iso === 'string' ? data.now_iso : '';
        const nextEntriesOpen = !!data.entries_open;
        const nextEntryFee = Number.isFinite(feeNum) ? feeNum : null;
        const nextDataVersion = dataVersion;

        const isSame =
          !prev.loading &&
          !prev.error &&
          prev.activeSeason === nextActiveSeason &&
          prev.entriesOpen === nextEntriesOpen &&
          prev.entryFee === nextEntryFee &&
          prev.dataVersion === nextDataVersion &&
          prev.availableSeasons.length === nextAvailableSeasons.length &&
          prev.availableSeasons.every((s, i) => s === nextAvailableSeasons[i]);

        if (isSame) return prev;

        return {
          activeSeason: nextActiveSeason,
          availableSeasons: nextAvailableSeasons,
          nowIso: nextNowIso,
          entriesOpen: nextEntriesOpen,
          entryFee: nextEntryFee,
          dataVersion: nextDataVersion,
          loading: false,
          error: ''
        };
      });
    } catch (e) {
      setState(prev => ({ ...prev, loading: false, error: e?.message || 'Failed to load site time' }));
    }
  }, []);

  useEffect(() => {
    refresh();

    let intervalId = null;

    const startPolling = () => {
      if (!intervalId) {
        intervalId = setInterval(() => {
          if (typeof document === 'undefined' || document.visibilityState === 'visible') {
            refresh();
          }
        }, 60_000);
      }
    };

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refresh();
        startPolling();
      } else {
        stopPolling();
      }
    };

    startPolling();
    if (typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      stopPolling();
      if (typeof document !== 'undefined' && document.removeEventListener) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [refresh]);

  const value = useMemo(() => ({ ...state, refresh }), [state, refresh]);

  return <SeasonContext.Provider value={value}>{children}</SeasonContext.Provider>;
}

export function useSeason() {
  return useContext(SeasonContext) || DEFAULT_SEASON_STATE;
}

export default SeasonContext;
